import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller } from "react-hook-form";
import { TEInput } from "tw-elements-react";
import { formatDatePickerValue } from "./form-utils";

export function CustomControlledDatePicker({
  placeholder,
  label,
  views,
  keyField,
  rules = {},
  control,
  error,
  errorMsg = "This field is required.",
}) {
  return (
    <Controller
      control={control}
      name={keyField}
      rules={rules}
      render={({ field: { onChange, onBlur, value, ref } }) => (
        <div className="input-wrap">
          <DatePicker
            onBlur={onBlur}
            placeholder={placeholder}
            onChange={(value) => onChange(formatDatePickerValue(value, views))}
            id={keyField}
            className={`${rules.required ? "required" : ""} !block`}
            required={rules.required}
            label={label}
            views={views}
            slotProps={{
              textField: {
                required: rules.required,
                InputLabelProps: {
                  classes: {
                    root: "test",
                  },
                },
                InputProps: {
                  classes: {
                    notchedOutline:
                      "!duration-200 transition-all ease-linear motion-reduce:transition-none outline-none border-0",
                  },
                },
                sx: {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.175)",
                  },
                  "& .MuiFormLabel-root": {
                    lineHeight: "1.6",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: "14px",
                    "@media (min-width: 1366px)": {
                      fontSize: "16px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "0.32rem 0.75rem",
                    height: "auto",
                    lineHeight: "1.6",
                    fontSize: "14px",
                    "@media (min-width: 1366px)": {
                      fontSize: "16px",
                    },
                  },
                  "& .MuiInputBase-input input": {
                    fontSize: "14px",
                    "@media (min-width: 1366px)": {
                      fontSize: "16px",
                    },
                  },
                  "& .MuiOutlinedInput-root2": {
                    minHeight: "36px", // Adjust the overall height
                  },
                },
              },
            }}
          />
          {rules.required && (
            <span className="text-[#e80000] text-sm font-thin mt-2 trans-ease-slow">
              {rules.required && error && errorMsg}
            </span>
          )}
        </div>
      )}
    />
  );
}

export function CustomInput({
  label,
  formatter,
  keyField,
  type,
  rules = {},
  register,
  error,
  errorMsg = "This field is required.",
}) {
  return (
    <div className="input-wrap">
      <TEInput
        type={type}
        id={keyField}
        name={keyField}
        label={label}
        onKeyUp={(e) => formatter && formatter(e)}
        className={`${rules.required ? "required" : ""}`}
        {...register(keyField, { ...rules })}
      ></TEInput>
      <span className="text-[#e80000] text-sm font-thin mt-2 trans-ease-slow">
        {rules.required && error && errorMsg}
      </span>
    </div>
  );
}

export function CustomControlledInput({
  label,
  keyField,
  rules = {},
  type,
  control,
  error,
  errorMsg = "This field is required.",
}) {
  return (
    <Controller
      control={control}
      name={keyField}
      rules={rules}
      render={({ field: { onChange, onBlur, value, ref } }) => (
        <div className="input-wrap">
          <TEInput
            type={type}
            label={label}
            onBlur={onBlur}
            selected={value}
            onChange={onChange}
            className={`${
              rules.required ? "required" : ""
            } transition-all duration-200 ease-linear motion-reduce:transition-none peer-focus:text-primary`}
            id={keyField}
          ></TEInput>
          {rules.required && (
            <span className="text-[#e80000] text-sm font-thin mt-2 trans-ease-slow">
              {rules.required && error && errorMsg}
            </span>
          )}
        </div>
      )}
    />
  );
}

export function CustomCheckbox({
  type,
  value,
  id,
  keyField,
  checked,
  label,
  onChange,
}) {
  const radioClass =
    "relative float-left -ml-[1.5rem] mr-1 mt-0.5 h-5 w-5 appearance-none rounded-full border-2 border-solid border-neutral-300 before:pointer-events-none before:absolute before:h-4 before:w-4 before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_13px_transparent] before:content-[''] after:absolute after:z-[1] after:block after:h-4 after:w-4 after:rounded-full after:content-[''] checked:border-primary checked:before:opacity-[0.16] checked:after:absolute checked:after:left-1/2 checked:after:top-1/2 checked:after:h-[0.625rem] checked:after:w-[0.625rem] checked:after:rounded-full checked:after:border-primary checked:after:bg-primary checked:after:content-[''] checked:after:[transform:translate(-50%,-50%)] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:shadow-none focus:outline-none focus:ring-0 focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] checked:focus:border-primary checked:focus:before:scale-100 checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] dark:border-neutral-600 dark:checked:border-primary dark:checked:after:border-primary dark:checked:after:bg-primary dark:focus:before:shadow-[0px_0px_0px_13px_rgba(255,255,255,0.4)] dark:checked:focus:border-primary dark:checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca]";

  const checkboxClass =
    "peer relative float-left mr-[6px] mt-[0.15rem] h-[1.125rem] w-[1.125rem] appearance-none rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300 outline-none before:pointer-events-none before:absolute before:h-[0.875rem] before:w-[0.875rem] before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_13px_transparent] before:content-[''] checked:border-primary checked:bg-primary checked:before:opacity-[0.16] checked:after:absolute checked:after:-mt-px checked:after:ml-[0.25rem] checked:after:block checked:after:h-[0.8125rem] checked:after:w-[0.375rem] checked:after:rotate-45 checked:after:border-[0.125rem] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-white checked:after:bg-transparent checked:after:content-[''] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:shadow-none focus:transition-[border-color_0.2s] focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] focus:after:absolute focus:after:z-[1] focus:after:block focus:after:h-[0.875rem] focus:after:w-[0.875rem] focus:after:rounded-[0.125rem] focus:after:content-[''] checked:focus:before:scale-100 checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] checked:focus:after:-mt-px checked:focus:after:ml-[0.25rem] checked:focus:after:h-[0.8125rem] checked:focus:after:w-[0.375rem] checked:focus:after:rotate-45 checked:focus:after:rounded-none checked:focus:after:border-[0.125rem] checked:focus:after:border-l-0 checked:focus:after:border-t-0 checked:focus:after:border-solid checked:focus:after:border-white checked:focus:after:bg-transparent dark:border-neutral-600 dark:checked:border-primary dark:checked:bg-primary dark:focus:before:shadow-[0px_0px_0px_13px_rgba(255,255,255,0.4)] dark:checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca]";

  const thisClass = type === "radio" ? radioClass : checkboxClass;

  return (
    <div
      className={`mb-[0.125rem] mr-4 inline-block min-h-[1.5rem] ${
        type === "radio" ? "pl-[1.5rem]" : ""
      }`}
    >
      <input
        className={thisClass}
        type={type}
        name={keyField}
        id={id}
        value={value}
        onChange={onChange}
        checked={checked}
      />
      <label
        className="mt-px inline-block pl-[0.15rem] hover:cursor-pointer font-normal text-subtitle"
        htmlFor={id}
      >
        {label || value}
      </label>
    </div>
  );
}
